/**
 * A character's voice (ADR-085's amendment for P1.9-T83, the author's ruling of 2026-10-03: "we need male and female
 * specific sounds for characters too"): which folder its hurt and death play from, `sfx/player/combat/<voice>/`. A
 * character has one of the listed voices or none, and none plays the shared folder. The list is the one place a voice
 * is named: a new voice is a new folder and one entry at the end.
 */

/**
 * One voice: its id, which is its folder's name, what the database stores and what a request names, and its label on
 * the Voice choice.
 */
export interface CharacterVoiceSpec {
  readonly id: string;
  readonly label: string;
}

/**
 * The voices, in the order the Voice choice offers them. A voice's place, counted from 1, is its number in a player's
 * appearance on the wire (`playerAppearance`), so the list is only ever added to at its end.
 */
export const CHARACTER_VOICES: readonly CharacterVoiceSpec[] = Object.freeze([
  { id: "masculine", label: "Masculine" },
  { id: "feminine", label: "Feminine" },
]);

/**
 * The label of having no voice on the Voice choice.
 */
export const NO_VOICE_LABEL = "None";

/**
 * The longest a voice's id may be, which the database's check holds a stored voice to.
 */
export const MAX_VOICE_CHARS = 32;

/**
 * The bits of a player's appearance that carry its voice; the rest are reserved and written as zero.
 */
const VOICE_BITS = 0xff;

/**
 * Whether a value is a listed voice.
 *
 * @param value Anything a request or a row carried.
 * @returns True for the id of a voice in `CHARACTER_VOICES`.
 */
export function isCharacterVoice(value: unknown): value is string {
  return typeof value === "string" && CHARACTER_VOICES.some((voice) => voice.id === value);
}

/**
 * Reads the voice a request names: an absent, null or empty field is no voice, a listed voice is itself, and anything
 * else is refused, so nothing but a listed voice is ever stored.
 *
 * @param value The request's `voice` field.
 * @returns The voice, null for none, or a refusal.
 */
export function voiceOfField(value: unknown): { readonly ok: true; readonly voice: string | null } | { readonly ok: false } {
  if (value === undefined || value === null || value === "") {
    return { ok: true, voice: null };
  }
  return isCharacterVoice(value) ? { ok: true, voice: value } : { ok: false };
}

/**
 * A player's appearance id on the wire (PROTOCOL.md section 5.2, from 6.17.0): its voice's place in the list from 1 in
 * the low byte, 0 for none.
 *
 * @param voice The character's voice, or null for none; one the list does not hold counts as none.
 * @returns The appearance id.
 * @example
 * playerAppearance("feminine");
 * // 2
 */
export function playerAppearance(voice: string | null): number {
  return voice === null ? 0 : CHARACTER_VOICES.findIndex((candidate) => candidate.id === voice) + 1;
}

/**
 * The voice a player's appearance id names: its low byte as a place in the list from 1.
 *
 * @param appearance The appearance id a spawn record carried.
 * @returns The voice, or null for 0, for a number past this page's list and for anything not a whole number from 0.
 */
export function voiceOfAppearance(appearance: number): string | null {
  if (!Number.isInteger(appearance) || appearance < 0) {
    return null;
  }
  return CHARACTER_VOICES[(appearance & VOICE_BITS) - 1]?.id ?? null;
}
